import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Input, Spin, message, Drawer, Select, DatePicker, Tooltip } from 'antd';
import { ExportOutlined, RedoOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { ButtonWrapper } from 'components';
import { ACL } from 'components/common';
import FilterTag from 'components/common/FilterTag';
import { FilterAtnButton } from 'utils/commonStyled';
import { AntdTable } from 'components/custom-table/Style';
import { Box, Flex } from 'atoms';
import { Tabs } from 'atoms/Tabs';
import { useQueryParams } from 'hooks';
import {
	getClientInvoiceListV2,
	exportClientInvoicesV2,
	getClientInvoiceDetailV2,
} from 'store/clientInvoiceSliceV2';
import { getFilterListV2 } from 'store/financeSliceV2';
import { getTableColumns } from './ClientInvoiceListPageV2.config';

const { Search } = Input;
const { RangePicker } = DatePicker;

const LtSearchBar = styled(Search)`
	border: none;
`;

const StyledFilterButton = styled(FilterAtnButton)`
	margin-left: 4px;
`;
const FilterDisplayBox = styled(Box)`
	flex: 1;
	min-width: 200px;
	height: 37px;
	padding: 10px 10px 10px 12px;
	background: #ffffff;
	border: 1px solid #c6c6c8;
	border-radius: 2px;
	display: flex;
	align-items: center;
	overflow-x: auto;
	white-space: nowrap;
	gap: 8px;
	&::-webkit-scrollbar {
		height: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: #c6c6c8;
		border-radius: 2px;
	}
`;

const FieldLabel = styled.div`
	font-size: 12px;
	font-weight: 600;
	color: #475569;
	margin: 16px 0 6px;
`;

const API_DATE = 'YYYY-MM-DD';
const PAYMENT_STATUS_FILTER = [
	{ value: 'Paid', label: 'Paid' },
	{ value: 'Partially Paid', label: 'Partially Paid' },
	{ value: 'Unpaid', label: 'Unpaid' },
];
const INVOICE_STATUS_FILTER = [
	{ value: 'Active', label: 'Active' },
	{ value: 'Cancellation Pending', label: 'Cancellation Pending' },
	{ value: 'Cancelled', label: 'Cancelled' },
];
const SHIPPING_METHOD_FILTER = [
	{ value: 'Road', label: 'Road' },
	{ value: 'Rail', label: 'Rail' },
	{ value: 'Water', label: 'Water' },
	{ value: 'Warehouse', label: 'Warehouse' },
];
const FILTER_KEYS = [
	'client_id',
	'project_id',
	'shipping_method',
	'payment_status',
	'invoice_status',
	'start_date',
	'end_date',
];
const toClientOptions = (rows = []) =>
	rows.map((r) => ({ value: r.id ?? r.value, label: r.label ?? r.name ?? r.value ?? '-' }));
const toProjectOptions = (rows = []) =>
	rows.map((r) => {
		const name = r.project_name ?? r.metadata?.project_name ?? r.label ?? r.value ?? '-';
		const code = r.metadata?.project_code;
		return {
			value: r.id ?? r.value,
			label: code && code !== name ? `${name} (${code})` : name,
		};
	});

function ClientInvoiceListPageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [config, setConfig] = useQueryParams({
		search_type: 'client_invoice',
		persistKey: 'client_invoice_list_filters',
	});

	const [invoices, setInvoices] = useState([]);
	const [loading, setLoading] = useState(false);
	const [activeTab, setActiveTab] = useState('all');
	const [searchValue, setSearchValue] = useState(config.search_term || '');
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [clientOptions, setClientOptions] = useState([]);
	const [projectOptions, setProjectOptions] = useState([]);
	const [total, setTotal] = useState(0);

	const currentPage = Number(config.currentPage) || 1;
	const pageSize = Number(config.limit) || 10;

	const fetchInvoices = useCallback(() => {
		setLoading(true);
		dispatch(
			getClientInvoiceListV2({
				filter_by: {
					client_id: config.client_id,
					project_id: config.project_id,
					shipping_method: config.shipping_method,
					payment_status: config.payment_status,
					invoice_status: config.invoice_status,
					start_date: config.start_date,
					end_date: config.end_date,
					search_term: config.search_term || undefined,
					...(activeTab === 'pending' ? { pending_approval: true } : {}),
				},
				pagination: { page: currentPage, per_page: pageSize },
			})
		)
			.then((res) => {
				setInvoices(res?.rows || []);
				setTotal(res?.count || 0);
			})
			.finally(() => setLoading(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, config, activeTab]);

	useEffect(() => {
		fetchInvoices();
	}, [fetchInvoices]);

	// Filter dropdown
	useEffect(() => {
		dispatch(getFilterListV2({ filter: 'client' }))
			.then((res) => setClientOptions(toClientOptions(res?.rows)))
			.catch(() => setClientOptions([]));
		dispatch(getFilterListV2({ filter: 'project' }))
			.then((res) => setProjectOptions(toProjectOptions(res?.rows)))
			.catch(() => setProjectOptions([]));
	}, [dispatch]);
	const applyPatch = (patch) =>
		setConfig((prev) => ({ ...prev, ...patch, currentPage: 1, offset: 0 }));
	const setFilter = (key, value) => applyPatch({ [key]: value || undefined });
	const clearFilter = (key) => applyPatch({ [key]: undefined });
	const clearDateRange = () => applyPatch({ start_date: undefined, end_date: undefined });
	const clearAllFilters = () =>
		applyPatch(FILTER_KEYS.reduce((acc, k) => ({ ...acc, [k]: undefined }), {}));

	const handleSearch = (value) => {
		setSearchValue(value);
		applyPatch({ search_term: value || undefined });
	};

	const handleTableChange = (paginationConfig) =>
		setConfig((prev) => ({
			...prev,
			currentPage: paginationConfig.current,
			limit: paginationConfig.pageSize,
			offset: (paginationConfig.current - 1) * paginationConfig.pageSize,
		}));

	const handleCreate = () => navigate('/client-invoices/new');
	const handleSync = () => fetchInvoices();
	const handleExport = () =>
		dispatch(
			exportClientInvoicesV2({
				filter_by: {
					client_id: config.client_id,
					project_id: config.project_id,
					shipping_method: config.shipping_method,
					payment_status: config.payment_status,
					invoice_status: config.invoice_status,
					start_date: config.start_date,
					end_date: config.end_date,
					search_term: config.search_term || undefined,
				},
			})
		);
	const handleView = (record) => navigate(`/client-invoices/${record.id || record.invoice_no}`);
	const handleRegenerate = (record) => navigate(`/client-invoices/${record.id}/regenerate`);
	const handleRowExport = (record) =>
		dispatch(exportClientInvoicesV2({ filter_by: { search_term: record.invoice_no } }));
	const openPdf = async (record) => {
		if (record?.pdf_url) {
			window.open(record.pdf_url);
			return;
		}
		if (!record?.id) {
			message.info('PDF is not available yet.');
			return;
		}
		const hide = message.loading('Preparing invoice PDF…', 0);
		try {
			const detail = await dispatch(getClientInvoiceDetailV2({ id: record.id }));
			const url = detail?.invoice?.pdf_url || detail?.pdf_url;
			if (url) {
				window.open(url);
			} else {
				message.info('PDF is not available for this invoice yet.');
			}
		} finally {
			hide();
		}
	};

	const dateRange =
		config.start_date && config.end_date
			? [moment(config.start_date), moment(config.end_date)]
			: null;
	const activeCount = [
		config.client_id,
		config.project_id,
		config.shipping_method,
		config.payment_status,
		config.invoice_status,
		config.start_date && config.end_date ? 'date' : undefined,
	].filter(Boolean).length;

	return (
		<Box>
			<Flex justifyContent="space-between" alignItems="center" mb={2}>
				<Flex alignItems="center" gap="6px" flex="1">
					<Box flex="0 0 22%">
						<LtSearchBar
							placeholder="Search invoice no / client..."
							allowClear
							onSearch={handleSearch}
							value={searchValue}
							onChange={(e) => setSearchValue(e.target.value)}
						/>
					</Box>
					<StyledFilterButton onClick={() => setDrawerOpen(true)}>
						FILTERS
					</StyledFilterButton>

					<FilterDisplayBox>
						{config.client_id && (
							<FilterTag onClose={() => clearFilter('client_id')}>Client</FilterTag>
						)}
						{config.project_id && (
							<FilterTag onClose={() => clearFilter('project_id')}>Project</FilterTag>
						)}
						{config.shipping_method && (
							<FilterTag onClose={() => clearFilter('shipping_method')}>
								Shipping Method
							</FilterTag>
						)}
						{config.payment_status && (
							<FilterTag onClose={() => clearFilter('payment_status')}>
								Payment Status
							</FilterTag>
						)}
						{config.invoice_status && (
							<FilterTag onClose={() => clearFilter('invoice_status')}>
								Invoice Status
							</FilterTag>
						)}
						{config.start_date && config.end_date && (
							<FilterTag onClose={clearDateRange}>Invoice Date</FilterTag>
						)}
						{activeCount > 0 && (
							<Tooltip title="Clear all filters">
								<CloseOutlined
									style={{
										color: '#ff4d4f',
										cursor: 'pointer',
										marginLeft: 'auto',
										marginRight: '4px',
									}}
									onClick={clearAllFilters}
								/>
							</Tooltip>
						)}
					</FilterDisplayBox>
				</Flex>
				<Flex gap="10px">
					<ButtonWrapper onClick={handleExport} icons={<ExportOutlined />}>
						EXPORT
					</ButtonWrapper>
					<ButtonWrapper onClick={handleSync} icons={<RedoOutlined />}>
						SYNC
					</ButtonWrapper>
					{activeTab === 'all' && (
						<ACL action="create" module="client_invoice">
							<ButtonWrapper onClick={handleCreate} icons={<PlusOutlined />}>
								CREATE INVOICE
							</ButtonWrapper>
						</ACL>
					)}
				</Flex>
			</Flex>

			<Tabs
				activeKey={activeTab}
				onChange={(key) => {
					setActiveTab(key);
					applyPatch({});
				}}
				items={[
					{ key: 'all', label: 'Client Invoice' },
					{ key: 'pending', label: 'Pending Client Invoice' },
				]}
			/>

			<Box mt={3}>
				<Spin spinning={loading}>
					<AntdTable
						rowKey={(record) => record.id || record.invoice_no}
						columns={getTableColumns({
							onView: handleView,
							onDownload: openPdf,
							onPdf: openPdf,
							onExport: handleRowExport,
							onRegenerate: handleRegenerate,
							showRegenerateButton: activeTab !== 'pending',
							showInvoiceStatus: activeTab !== 'pending',
						})}
						dataSource={invoices}
						pagination={{
							current: currentPage,
							pageSize,
							total,
							showSizeChanger: true,
							pageSizeOptions: ['10', '20', '50', '100'],
							showTotal: (t) => `Total ${t} invoices`,
						}}
						onChange={handleTableChange}
					/>
				</Spin>
			</Box>

			<Drawer
				title="Filters"
				placement="right"
				width={360}
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
			>
				<FieldLabel>Client</FieldLabel>
				<Select
					style={{ width: '100%' }}
					allowClear
					showSearch
					optionFilterProp="label"
					placeholder="Select client"
					options={clientOptions}
					value={config.client_id}
					onChange={(v) => setFilter('client_id', v)}
				/>

				<FieldLabel>Project</FieldLabel>
				<Select
					style={{ width: '100%' }}
					allowClear
					showSearch
					optionFilterProp="label"
					placeholder="Select project"
					options={projectOptions}
					value={config.project_id}
					onChange={(v) => setFilter('project_id', v)}
				/>

				<FieldLabel>Shipping Method</FieldLabel>
				<Select
					style={{ width: '100%' }}
					allowClear
					placeholder="Select shipping method"
					options={SHIPPING_METHOD_FILTER}
					value={config.shipping_method}
					onChange={(v) => setFilter('shipping_method', v)}
				/>

				<FieldLabel>Invoice Date</FieldLabel>
				<RangePicker
					style={{ width: '100%' }}
					format="DD-MM-YYYY"
					value={dateRange}
					onChange={(range) =>
						applyPatch({
							start_date: range?.[0] ? range[0].format(API_DATE) : undefined,
							end_date: range?.[1] ? range[1].format(API_DATE) : undefined,
						})
					}
				/>

				<FieldLabel>Payment Status</FieldLabel>
				<Select
					style={{ width: '100%' }}
					allowClear
					placeholder="Select payment status"
					options={PAYMENT_STATUS_FILTER}
					value={config.payment_status}
					onChange={(v) => setFilter('payment_status', v)}
				/>

				<FieldLabel>Invoice Status</FieldLabel>
				<Select
					style={{ width: '100%' }}
					allowClear
					placeholder="Select invoice status"
					options={INVOICE_STATUS_FILTER}
					value={config.invoice_status}
					onChange={(v) => setFilter('invoice_status', v)}
				/>
			</Drawer>
		</Box>
	);
}

export default ClientInvoiceListPageV2;
